import { useState, useEffect } from "react";
import { Bluetooth, BatteryFull, WifiHigh, WifiLow, WifiSlash, Lock, LockOpen, Thermometer, MapPin, Lightning, ArrowClockwise, Play, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function rssiToSignal(rssi) {
  if (rssi >= -50) return { label: "Excelente", color: "#22c55e", Icon: WifiHigh };
  if (rssi >= -65) return { label: "Bom", color: "var(--success)", Icon: WifiHigh };
  if (rssi >= -75) return { label: "Medio", color: "var(--warning)", Icon: WifiLow };
  return { label: "Fraco", color: "var(--error)", Icon: WifiSlash };
}

function batteryColor(level) {
  if (level >= 60) return "var(--success)";
  if (level >= 30) return "var(--warning)";
  return "var(--error)";
}

function BeaconCard({ beacon, isSelected, onSelect }) {
  const signal = rssiToSignal(beacon.rssi);
  return (
    <div
      data-testid={`beacon-card-${beacon.bag_code}`}
      className="bento-item p-5 cursor-pointer card-hover"
      onClick={() => onSelect(beacon)}
      style={{ borderColor: isSelected ? "var(--primary)" : "var(--border-color)", borderWidth: isSelected ? 2 : 1 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: beacon.status === "online" ? "#22c55e" : "var(--text-muted)" }} />
          <span className="bag-code">{beacon.bag_code}</span>
        </div>
        <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.65rem", color: "var(--text-muted)" }}>{beacon.device_id}</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="flex items-center gap-1" style={{ fontSize: "0.75rem" }}>
          <signal.Icon size={14} style={{ color: signal.color }} />
          <span style={{ color: signal.color, fontWeight: 600 }}>{beacon.rssi}dBm</span>
        </div>
        <div className="flex items-center gap-1" style={{ fontSize: "0.75rem" }}>
          <BatteryFull size={14} style={{ color: batteryColor(beacon.battery_level) }} />
          <span style={{ color: batteryColor(beacon.battery_level), fontWeight: 600 }}>{beacon.battery_level}%</span>
        </div>
        <div className="flex items-center gap-1" style={{ fontSize: "0.75rem" }}>
          {beacon.lock_status === "locked" ? <Lock size={14} style={{ color: "var(--primary)" }} /> : <LockOpen size={14} style={{ color: "var(--warning)" }} />}
          <span style={{ fontWeight: 600, color: beacon.lock_status === "locked" ? "var(--primary)" : "var(--warning)" }}>
            {beacon.lock_status === "locked" ? "Trancado" : "Aberto"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function BeaconDashboard() {
  const [beacons, setBeacons] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [commandResult, setCommandResult] = useState(null);

  const fetchData = async () => {
    try {
      const [beaconsRes, statsRes] = await Promise.all([
        axios.get(`${API}/beacons/`),
        axios.get(`${API}/beacons/stats/overview`),
      ]);
      setBeacons(beaconsRes.data);
      setStats(statsRes.data);
      if (selected) {
        const updated = beaconsRes.data.find(b => b.bag_code === selected.bag_code);
        if (updated) setSelected(updated);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const simulateBeacons = async () => {
    setSimulating(true);
    try {
      await axios.post(`${API}/beacons/simulate`);
      await fetchData();
    } catch (e) { console.error(e); }
    finally { setSimulating(false); }
  };

  const sendCommand = async (bagCode, command) => {
    try {
      const res = await axios.post(`${API}/beacons/command`, { bag_code: bagCode, command });
      setCommandResult({ success: true, message: `Comando "${command}" enviado para ${bagCode}` });
      await fetchData();
      setTimeout(() => setCommandResult(null), 3000);
    } catch (e) {
      setCommandResult({ success: false, message: "Erro ao enviar comando" });
    }
  };

  return (
    <div data-testid="beacon-dashboard" className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 4 }}>ESP32 Beacons</span>
          <h1 style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700 }}>Dispositivos Bluetooth</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Monitoramento e controlo dos beacons das bolsas</p>
        </div>
        <button
          data-testid="simulate-beacons-btn"
          onClick={simulateBeacons}
          disabled={simulating}
          className="flex items-center gap-2"
          style={{ background: "var(--primary)", color: "white", padding: "10px 22px", borderRadius: 9999, border: "none", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem", opacity: simulating ? 0.6 : 1 }}
        >
          {simulating ? <ArrowClockwise size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
          {simulating ? "A simular..." : "Simular atividade"}
        </button>
      </div>

      {/* Command result toast */}
      {commandResult && (
        <div className="animate-fade-in mb-4" style={{ padding: "10px 16px", borderRadius: 10, display: "flex", alignItems: "center", gap: 8, background: commandResult.success ? "rgba(62,102,86,0.1)" : "rgba(184,74,74,0.1)", border: `1px solid ${commandResult.success ? "var(--success)" : "var(--error)"}` }}>
          {commandResult.success ? <CheckCircle size={18} weight="fill" style={{ color: "var(--success)" }} /> : <WarningCircle size={18} weight="fill" style={{ color: "var(--error)" }} />}
          <span style={{ fontSize: "0.85rem", fontWeight: 500, color: commandResult.success ? "var(--success)" : "var(--error)" }}>{commandResult.message}</span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Bluetooth, value: stats.total_beacons, label: "Total beacons", color: "var(--primary)" },
            { icon: WifiHigh, value: stats.online, label: "Online", color: "#22c55e" },
            { icon: BatteryFull, value: `${stats.avg_battery}%`, label: "Bateria media", color: batteryColor(stats.avg_battery) },
            { icon: Lock, value: stats.locked, label: "Trancados", color: "var(--primary)" },
          ].map((s, i) => (
            <div key={i} className="bento-item p-5 card-hover">
              <s.icon size={22} weight="duotone" style={{ color: s.color, marginBottom: 8 }} />
              <span style={{ fontFamily: "Outfit", fontSize: "1.6rem", fontWeight: 700, display: "block" }}>{s.value}</span>
              <span className="overline">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Beacon list */}
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-3" style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto", paddingRight: 4 }}>
            {beacons.map(b => (
              <BeaconCard key={b.bag_code} beacon={b} isSelected={selected?.bag_code === b.bag_code} onSelect={setSelected} />
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {selected ? (
            <div className="animate-fade-in" data-testid="beacon-detail">
              <div className="bento-item p-6 mb-4">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: selected.status === "online" ? "#22c55e" : "var(--text-muted)" }} />
                      <span className="bag-code" style={{ fontSize: "1.3rem" }}>{selected.bag_code}</span>
                    </div>
                    <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.75rem", color: "var(--text-muted)" }}>{selected.device_id} | FW {selected.firmware_version || "2.1.0"}</span>
                  </div>
                  <span className={`status-${selected.status}`} style={{ padding: "4px 12px", borderRadius: 9999, fontSize: "0.75rem", fontWeight: 600 }}>
                    {selected.status === "online" ? "Online" : "Standby"}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <div style={{ padding: 12, borderRadius: 10, background: "var(--bg)", textAlign: "center" }}>
                    <WifiHigh size={18} weight="duotone" style={{ color: rssiToSignal(selected.rssi).color, margin: "0 auto 4px" }} />
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, display: "block" }}>{selected.rssi} dBm</span>
                    <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Sinal</span>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: "var(--bg)", textAlign: "center" }}>
                    <BatteryFull size={18} weight="duotone" style={{ color: batteryColor(selected.battery_level), margin: "0 auto 4px" }} />
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, display: "block" }}>{selected.battery_level}%</span>
                    <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Bateria</span>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: "var(--bg)", textAlign: "center" }}>
                    <Thermometer size={18} weight="duotone" style={{ color: "var(--warning)", margin: "0 auto 4px" }} />
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, display: "block" }}>{selected.temperature || "--"}°C</span>
                    <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Temperatura</span>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: "var(--bg)", textAlign: "center" }}>
                    {selected.lock_status === "locked" ? <Lock size={18} weight="duotone" style={{ color: "var(--primary)", margin: "0 auto 4px" }} /> : <LockOpen size={18} weight="duotone" style={{ color: "var(--warning)", margin: "0 auto 4px" }} />}
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, display: "block" }}>{selected.lock_status === "locked" ? "Trancado" : "Aberto"}</span>
                    <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>Trinco</span>
                  </div>
                </div>

                {/* GPS */}
                {selected.lat && selected.lng && (
                  <div className="flex items-center gap-2 mb-4" style={{ padding: "8px 12px", borderRadius: 8, background: "var(--bg)", fontSize: "0.8rem" }}>
                    <MapPin size={16} weight="duotone" style={{ color: "var(--primary)" }} />
                    <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.75rem" }}>
                      {selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}
                    </span>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.7rem", marginLeft: "auto" }}>
                      Ultimo ping: {new Date(selected.last_ping).toLocaleString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                  </div>
                )}

                {/* Commands */}
                <div>
                  <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 8 }}>Comandos</h4>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { cmd: selected.lock_status === "locked" ? "unlock" : "lock", label: selected.lock_status === "locked" ? "Destrancar" : "Trancar", icon: selected.lock_status === "locked" ? LockOpen : Lock, color: "var(--primary)" },
                      { cmd: "ping", label: "Ping", icon: WifiHigh, color: "#2563eb" },
                      { cmd: "locate", label: "Localizar", icon: MapPin, color: "var(--accent)" },
                    ].map(c => (
                      <button
                        key={c.cmd}
                        data-testid={`beacon-cmd-${c.cmd}`}
                        onClick={() => sendCommand(selected.bag_code, c.cmd)}
                        className="flex items-center gap-2 card-hover"
                        style={{ padding: "8px 16px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--surface)", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, fontFamily: "Manrope", transition: "all 0.2s" }}
                      >
                        <c.icon size={16} weight="duotone" style={{ color: c.color }} />
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Signal History */}
              {selected.signal_history && selected.signal_history.length > 0 && (
                <div className="bento-item p-6">
                  <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 10 }}>Historico de sinal</h4>
                  <div className="flex items-end gap-1" style={{ height: 80 }}>
                    {selected.signal_history.slice(-20).map((s, i) => {
                      const normalized = Math.max(0, Math.min(100, (s.rssi + 90) * 2));
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <div style={{ width: "100%", height: `${normalized}%`, background: rssiToSignal(s.rssi).color, borderRadius: "3px 3px 0 0", minHeight: 4, transition: "height 0.3s ease" }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span style={{ fontSize: "0.6rem", color: "var(--text-muted)" }}>-60min</span>
                    <span style={{ fontSize: "0.6rem", color: "var(--text-muted)" }}>agora</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bento-item p-12 text-center animate-fade-in">
              <Bluetooth size={48} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 12px" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>Selecione um beacon</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Clique num dispositivo para ver detalhes e enviar comandos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
